import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, Menu, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { isValidHttpUrl, normalizePortalMenuTarget } from '@/lib/portal-menu';
import {
  useProviderCustomerPortalMenuSettings,
  useUpdateProviderCustomerPortalMenuSettings,
} from '@/hooks/provider/useProviderCustomerPortalMenuSettings';
import { ProviderCustomerPortalMenuRequestError } from '@/services/provider/customer-portal-menu.service';
import type { CustomerPortalMenuItem, CustomerPortalMenuTarget } from '@/types/customer-portal-menu';

const MAX_ITEMS = 20;
const MAX_LABEL_LENGTH = 80;
const MAX_URL_LENGTH = 2048;
const MAX_ICON_LENGTH = 80;
const MAX_ORDER = 10000;

type MenuItemForm = {
  label: string;
  url: string;
  icon: string;
  order: number | '';
  target: CustomerPortalMenuTarget;
};

type FormValues = {
  items: MenuItemForm[];
};

const buildMenuItem = (item?: CustomerPortalMenuItem | null): MenuItemForm => ({
  label: item?.label ?? '',
  url: item?.url ?? '',
  icon: item?.icon ?? '',
  order: item?.order ?? '',
  target: normalizePortalMenuTarget(item?.target),
});

const normalizeOrderForPayload = (value: MenuItemForm['order']): number | null => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }
  return value;
};

export function ProviderCustomerPortalMenuCard() {
  const { t } = useTranslation();
  const settingsQuery = useProviderCustomerPortalMenuSettings();
  const updateMutation = useUpdateProviderCustomerPortalMenuSettings();

  const schema = useMemo(
    () =>
      z.object({
        items: z
          .array(
            z.object({
              label: z
                .string()
                .trim()
                .min(1, {
                  message: t(
                    'settings.portalMenu.validation.labelRequired',
                    'Enter a label.',
                  ),
                })
                .max(MAX_LABEL_LENGTH, {
                  message: t(
                    'settings.portalMenu.validation.labelTooLong',
                    'Label must be 80 characters or fewer.',
                  ),
                }),
              url: z
                .string()
                .trim()
                .min(1, {
                  message: t(
                    'settings.portalMenu.validation.urlRequired',
                    'Enter a URL.',
                  ),
                })
                .max(MAX_URL_LENGTH, {
                  message: t(
                    'settings.portalMenu.validation.urlTooLong',
                    'URL must be 2048 characters or fewer.',
                  ),
                })
                .refine((value) => isValidHttpUrl(value), {
                  message: t(
                    'settings.portalMenu.validation.urlInvalid',
                    'Enter a valid http(s) URL.',
                  ),
                }),
              icon: z
                .string()
                .trim()
                .max(MAX_ICON_LENGTH, {
                  message: t(
                    'settings.portalMenu.validation.iconTooLong',
                    'Icon must be 80 characters or fewer.',
                  ),
                })
                .optional(),
              order: z.preprocess(
                (value) => {
                  if (value === '' || value === null || value === undefined) {
                    return undefined;
                  }
                  const parsed = typeof value === 'number' ? value : Number(value);
                  return Number.isNaN(parsed) ? value : parsed;
                },
                z
                  .number({
                    invalid_type_error: t(
                      'settings.portalMenu.validation.orderInteger',
                      'Order must be a whole number.',
                    ),
                  })
                  .int({
                    message: t(
                      'settings.portalMenu.validation.orderInteger',
                      'Order must be a whole number.',
                    ),
                  })
                  .min(0, {
                    message: t(
                      'settings.portalMenu.validation.orderRange',
                      'Order must be between 0 and 10000.',
                    ),
                  })
                  .max(MAX_ORDER, {
                    message: t(
                      'settings.portalMenu.validation.orderRange',
                      'Order must be between 0 and 10000.',
                    ),
                  })
                  .optional(),
              ),
              target: z.enum(['_blank', '_self']).optional(),
            }),
          )
          .max(MAX_ITEMS, {
            message: t(
              'settings.portalMenu.validation.maxItems',
              'You can add up to 20 menu items.',
            ),
          }),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'items',
  });

  useEffect(() => {
    if (settingsQuery.data) {
      const nextItems = settingsQuery.data.items.map((item) => buildMenuItem(item));
      form.reset({ items: nextItems });
    }
  }, [form, settingsQuery.data]);

  const isSaving = updateMutation.isPending;

  const handleAddItem = () => {
    if (fields.length >= MAX_ITEMS) {
      toast.error(
        t(
          'settings.portalMenu.validation.maxItems',
          'You can add up to 20 menu items.',
        ),
      );
      return;
    }

    append(buildMenuItem());
  };

  const handleClear = () => {
    form.reset({ items: [] });
  };

  const handleSubmit = async (values: FormValues) => {
    const payloadItems: CustomerPortalMenuItem[] = values.items.map((item) => {
      const icon = item.icon?.trim();
      return {
        label: item.label.trim(),
        url: item.url.trim(),
        icon: icon ? icon : null,
        order: normalizeOrderForPayload(item.order),
        target: normalizePortalMenuTarget(item.target),
      };
    });

    try {
      const updated = await updateMutation.mutateAsync({ items: payloadItems });
      form.reset({ items: updated.items.map((item) => buildMenuItem(item)) });
      toast.success(
        t('settings.portalMenu.messages.saveSuccess', 'Customer portal menu saved.'),
      );
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : t(
              'settings.portalMenu.messages.saveError',
              'Unable to save customer portal menu.',
            );

      form.clearErrors();
      if (
        error instanceof ProviderCustomerPortalMenuRequestError &&
        error.errors?.items?.[0]
      ) {
        form.setError('items', { type: 'server', message: error.errors.items[0] });
      } else {
        form.setError('root', { type: 'server', message });
      }

      toast.error(message);
    }
  };

  if (settingsQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Menu className="h-5 w-5 text-muted-foreground" />
            {t('settings.portalMenu.title', 'Customer portal menu')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.portalMenu.description',
              'Add custom links to the customer portal navigation.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-40" />
        </CardContent>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {t('settings.portalMenu.title', 'Customer portal menu')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.portalMenu.description',
              'Add custom links to the customer portal navigation.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTitle>
              {t(
                'settings.portalMenu.messages.loadError',
                'Unable to load customer portal menu.',
              )}
            </AlertTitle>
            <AlertDescription className="mt-2 flex items-center gap-3">
              <span>{settingsQuery.error.message}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => settingsQuery.refetch()}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {t('common.retry', 'Retry')}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Menu className="h-5 w-5 text-muted-foreground" />
          {t('settings.portalMenu.title', 'Customer portal menu')}
        </CardTitle>
        <CardDescription>
          {t(
            'settings.portalMenu.description',
            'Add custom links to the customer portal navigation.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form className="space-y-5" onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>
                {t(
                  'settings.portalMenu.helper',
                  'Links appear in the customer portal sidebar. Only http/https URLs are supported.',
                )}
              </p>
              <p>
                {t(
                  'settings.portalMenu.hints',
                  'Lower order numbers appear first. Use icon names like help-circle.',
                )}
              </p>
              <p>
                {t(
                  'settings.portalMenu.iconLibrary',
                  'Icon names come from Lucide (lucide-react). Browse:',
                )}{' '}
                <a
                  href={t('settings.portalMenu.iconLibraryUrl', 'https://lucide.dev/icons')}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  {t('settings.portalMenu.iconLibraryLinkLabel', 'lucide.dev/icons')}
                </a>
              </p>
            </div>

            {form.formState.errors.items?.message && (
              <Alert variant="destructive">
                <AlertDescription>{form.formState.errors.items.message}</AlertDescription>
              </Alert>
            )}

            {form.formState.errors.root?.message && (
              <Alert variant="destructive">
                <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
              </Alert>
            )}

            {fields.length === 0 ? (
              <div className="rounded-lg border border-dashed px-4 py-6 text-sm text-muted-foreground">
                {t(
                  'settings.portalMenu.empty',
                  'No menu items yet. Add up to 20 links.',
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="rounded-xl border bg-muted/20 p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold">
                        {t('settings.portalMenu.itemTitle', {
                          index: index + 1,
                          defaultValue: 'Menu item {{index}}',
                        })}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(index)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name={`items.${index}.label`}
                        render={({ field: itemField }) => (
                          <FormItem>
                            <FormLabel>
                              {t('settings.portalMenu.fields.label', 'Label')}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t(
                                  'settings.portalMenu.placeholders.label',
                                  'Support',
                                )}
                                {...itemField}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`items.${index}.url`}
                        render={({ field: itemField }) => (
                          <FormItem>
                            <FormLabel>
                              {t('settings.portalMenu.fields.url', 'URL')}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t(
                                  'settings.portalMenu.placeholders.url',
                                  'https://support.example.com',
                                )}
                                inputMode="url"
                                {...itemField}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="mt-4 grid gap-4 md:grid-cols-3">
                      <FormField
                        control={form.control}
                        name={`items.${index}.icon`}
                        render={({ field: itemField }) => (
                          <FormItem>
                            <FormLabel>
                              {t('settings.portalMenu.fields.icon', 'Icon')}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t(
                                  'settings.portalMenu.placeholders.icon',
                                  'help-circle',
                                )}
                                {...itemField}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`items.${index}.order`}
                        render={({ field: itemField }) => (
                          <FormItem>
                            <FormLabel>
                              {t('settings.portalMenu.fields.order', 'Order')}
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={0}
                                max={MAX_ORDER}
                                placeholder={t(
                                  'settings.portalMenu.placeholders.order',
                                  '10',
                                )}
                                value={itemField.value === '' ? '' : itemField.value}
                                onChange={(event) => itemField.onChange(event.target.value)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`items.${index}.target`}
                        render={({ field: itemField }) => (
                          <FormItem>
                            <FormLabel>
                              {t('settings.portalMenu.fields.target', 'Target')}
                            </FormLabel>
                            <Select
                              value={itemField.value}
                              onValueChange={itemField.onChange}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={t(
                                      'settings.portalMenu.placeholders.target',
                                      'Open in new tab',
                                    )}
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="_blank">
                                  {t(
                                    'settings.portalMenu.targets.blank',
                                    'Open in new tab',
                                  )}
                                </SelectItem>
                                <SelectItem value="_self">
                                  {t(
                                    'settings.portalMenu.targets.self',
                                    'Open in same tab',
                                  )}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleAddItem}
                className="gap-2"
                disabled={isSaving}
              >
                <Plus className="h-4 w-4" />
                {t('settings.portalMenu.actions.add', 'Add menu item')}
              </Button>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClear}
                  disabled={isSaving || fields.length === 0}
                >
                  {t('settings.portalMenu.actions.clear', 'Clear menu')}
                </Button>
                <Button type="submit" disabled={isSaving} className="gap-2">
                  {isSaving && <CheckCircle2 className="h-4 w-4 animate-spin" />}
                  {t('settings.portalMenu.actions.save', 'Save menu')}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
